import { useCallback, useMemo } from 'react';

import {
  BalancePool,
  BalancePoolCategory,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';

export const useRemainingBalance = (props: { balancePools: BalancePool[] }) => {
  const { balancePools } = props;

  const filterPoolsByCategory = useCallback(
    (pools: BalancePool[], category: string | BalancePoolCategory) => {
      return pools.filter((p) => p.category === category && p.balance > 0);
    },
    [],
  );

  const removePoolReasons = useCallback((pools: BalancePool[]) => {
    return pools.map((p): BalancePool => {
      const { reasons, ...rest } = p;
      return rest;
    });
  }, []);

  const combinePoolsByName = useCallback((pools: BalancePool[]) => {
    return pools.reduce((acc, pool) => {
      // Combine multiple balances for same year
      const prevIndex = acc.findIndex((p) => p.name === pool.name);
      if (prevIndex > -1) {
        const prevPool = acc.splice(prevIndex, 1)[0];

        return [
          ...acc,
          {
            ...prevPool,
            balance: prevPool.balance + pool.balance,
          },
        ];
      }

      return [...acc, pool];
    }, [] as BalancePool[]);
  }, []);

  const poolGroups = useMemo(() => {
    const remainingPools: BalancePool[] = balancePools.filter(
      (p) => p.balance > 0,
    );

    const hasMultiplePools = remainingPools.length > 1;
    const hasVacationBalancePool = !!remainingPools.find(
      (p) => p.category === BalancePoolCategory.VacationBalance,
    );
    const isOnlyPoolNotVacationBalance =
      remainingPools.length === 1 && !hasVacationBalancePool;

    return {
      isExpandListEnabled: hasMultiplePools || isOnlyPoolNotVacationBalance,
      vacationBalancePools: filterPoolsByCategory(
        balancePools,
        BalancePoolCategory.VacationBalance,
      ),
      extraVacationBalancePools: filterPoolsByCategory(
        balancePools,
        BalancePoolCategory.ExtraVacationBalance,
      ),
      yearlyVacationsPools: removePoolReasons(
        combinePoolsByName(
          filterPoolsByCategory(
            balancePools,
            BalancePoolCategory.YearlyVacationBalance,
          ),
        ),
      ),
      vacationBanksPools: removePoolReasons(
        filterPoolsByCategory(
          balancePools,
          BalancePoolCategory.VacationBankBalance,
        ),
      ),
    };
  }, [
    balancePools,
    filterPoolsByCategory,
    removePoolReasons,
    combinePoolsByName,
  ]);

  return {
    ...poolGroups,
  };
};
